import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Typography, Divider, Chip } from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import Colors from "../../components/ui/Colors";
import Button from '@mui/material/Button';
import { useHistory} from "react-router-dom";

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const dateOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
};

const EXPIRY_WARNING_DAYS = 30;

function expiryColor(expirationDate) {
    if (!expirationDate) return "inherit";
    const now = new Date();
    const exp = new Date(expirationDate);
    const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "error.main";
    if (diffDays <= EXPIRY_WARNING_DAYS) return "warning.main";
    return "text.primary";
}

export default function TransportOrderBatches(props) {

    const history = useHistory();
    const [loading, setLoading] = React.useState(false);
    const [batchesList, setBatchesList] = React.useState("");
    const [individualsList, setIndividualsList] = React.useState("");

    React.useEffect(() => {
        renderBatchesList();
        renderIndividualsList();
    }, []);

    const getDeliveryOrders = () => {
        if (!props.location.state || !props.location.state.order || !props.location.state.order.deliveryOrderResponse) {
            return null;
        }
        return props.location.state.order.deliveryOrderResponse;
    };

    const order = props.location.state && props.location.state.order;

    const renderBatchesList = () => {
        setLoading(true);
        const deliveryOrders = getDeliveryOrders();
        if (!deliveryOrders) { setLoading(false); return; }

        // Collect all dispatched batches across all delivery orders
        const allBatches = deliveryOrders.flatMap(o =>
            (o.productBatchesResponse || []).filter(b => b.inWarehouse === false)
        );

        // Group by product description
        const grouped = allBatches.reduce((acc, batch) => {
            const key = batch.warehouseProduct ? batch.warehouseProduct.description : "Sin descripción";
            if (!acc[key]) acc[key] = [];
            acc[key].push(batch);
            return acc;
        }, {});

        const sortedKeys = Object.keys(grouped).sort();

        setBatchesList(
            <Box sx={{width: "100%"}}>
                {sortedKeys.map((productDesc) => {
                    const batches = grouped[productDesc];
                    const totalUnits = batches.reduce((sum, b) => sum + parseFloat(b.productAmount || 0), 0);

                    return (
                        <Box key={productDesc} sx={{mb: 3}}>
                            {/* Product group header */}
                            <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 1, px: 1, py: 0.5, backgroundColor: "#f5f5f5", borderRadius: 1}}>
                                <Typography variant="body2" sx={{fontWeight: "bold", flex: 1, textAlign: "left"}}>
                                    {productDesc}
                                </Typography>
                                <Chip
                                    label={`${batches.length} estiba${batches.length !== 1 ? "s" : ""} · ${new Intl.NumberFormat('es-CO').format(totalUnits)} uds`}
                                    size="small"
                                    sx={{backgroundColor: Colors.primary, color: "white", fontWeight: "bold"}}
                                />
                            </Box>

                            {/* Column headers */}
                            <Grid container spacing={1} sx={{px: 1, mb: 0.5}}>
                                <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Lote</Typography>
                                </Grid>
                                <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Fc. Producción</Typography>
                                </Grid>
                                <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Fc. Vencimiento</Typography>
                                </Grid>
                                <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Cantidad</Typography>
                                </Grid>
                                <Grid item xs={1} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>ID</Typography>
                                </Grid>
                            </Grid>

                            {/* Batch rows */}
                            <List disablePadding>
                                {batches.map((batch) => (
                                    <ListItem key={batch.id} disablePadding divider sx={{py: 0.5}}>
                                        <Grid container spacing={1} sx={{px: 1}}>
                                            <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2">{batch.batchCode}</Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2">
                                                    {new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(batch.date))}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2" sx={{color: expiryColor(batch.expirationDate), fontWeight: expiryColor(batch.expirationDate) !== "text.primary" ? "bold" : "normal"}}>
                                                    {new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(batch.expirationDate))}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2">{new Intl.NumberFormat('es-CO').format(batch.productAmount)}</Typography>
                                            </Grid>
                                            <Grid item xs={1} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2" sx={{color: "text.secondary"}}>{batch.id}</Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    );
                })}
            </Box>
        );

        setLoading(false);
    };

    const renderIndividualsList = () => {
        const deliveryOrders = getDeliveryOrders();
        if (!deliveryOrders) return;

        const allIndividuals = deliveryOrders.flatMap(o =>
            (o.deliveryOrderIndividualsResponse || []).filter(i => parseFloat(i.productAmount || 0) > 0)
        );

        if (allIndividuals.length === 0) return;

        // Group by description
        const grouped = allIndividuals.reduce((acc, item) => {
            const key = item.description || "Sin descripción";
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {});

        const sortedKeys = Object.keys(grouped).sort();

        setIndividualsList(
            <Box sx={{width: "100%"}}>
                {sortedKeys.map((desc) => {
                    const items = grouped[desc];
                    const totalUnits = items.reduce((sum, i) => sum + parseFloat(i.productAmount || 0), 0);

                    return (
                        <Box key={desc} sx={{mb: 3}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 1, px: 1, py: 0.5, backgroundColor: "#f5f5f5", borderRadius: 1}}>
                                <Typography variant="body2" sx={{fontWeight: "bold", flex: 1, textAlign: "left"}}>
                                    {desc}
                                </Typography>
                                <Chip
                                    label={`${items.length} paquete${items.length !== 1 ? "s" : ""} · ${new Intl.NumberFormat('es-CO').format(totalUnits)} uds`}
                                    size="small"
                                    sx={{backgroundColor: Colors.secondary, color: "white", fontWeight: "bold"}}
                                />
                            </Box>

                            <Grid container spacing={1} sx={{px: 1, mb: 0.5}}>
                                <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Lote</Typography>
                                </Grid>
                                <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Fc. Producción</Typography>
                                </Grid>
                                <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Fc. Vencimiento</Typography>
                                </Grid>
                                <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>Cantidad</Typography>
                                </Grid>
                                <Grid item xs={1} sx={{display: "flex", justifyContent: "center"}}>
                                    <Typography variant="caption" sx={{fontWeight: "bold", color: "text.secondary"}}>ID</Typography>
                                </Grid>
                            </Grid>

                            <List disablePadding>
                                {items.map((item) => (
                                    <ListItem key={item.batchId} disablePadding divider sx={{py: 0.5}}>
                                        <Grid container spacing={1} sx={{px: 1}}>
                                            <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2">{item.batchCode}</Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2">
                                                    {new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(item.date))}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2" sx={{color: expiryColor(item.expirationDate), fontWeight: expiryColor(item.expirationDate) !== "text.primary" ? "bold" : "normal"}}>
                                                    {new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(item.expirationDate))}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2">{new Intl.NumberFormat('es-CO').format(item.productAmount)}</Typography>
                                            </Grid>
                                            <Grid item xs={1} sx={{display: "flex", justifyContent: "center"}}>
                                                <Typography variant="body2" sx={{color: "text.secondary"}}>{item.batchId}</Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const goBack = () => {
        history.push({
            pathname: "/warehouse/transport-order",
            state: { order: props.location.state.order },
        });
    };

    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: {xs: "start", sm: "center"},
                width: "90%",
                height: "100%",
                textAlign: "center",
                overflowY: "scroll",
            }}>
                {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> :
                    <Grid container>

                        {/* Title + context */}
                        <Grid item xs={12} sx={{mt: 2, mb: 1, textAlign: "center"}}>
                            <Typography variant="h6" sx={{fontWeight: "bold", color: Colors.primary}}>
                                Estibas del Despacho
                            </Typography>
                            {order && (
                                <Typography variant="body2" sx={{color: "text.secondary", mt: 0.5}}>
                                    {`Doc. ${order.id}`}
                                    {order.deliveryOrderResponse && order.deliveryOrderResponse.length > 0
                                        ? ` · ${Array.from(new Set(order.deliveryOrderResponse.map(o => o.clientName).filter(Boolean))).join(", ")}`
                                        : ""}
                                    {order.destination ? ` · ${order.destination}` : ""}
                                    {order.carPlate ? ` · ${order.carPlate}` : ""}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12} sx={{mb: 2}}>
                            <Divider />
                        </Grid>

                        {/* Batches */}
                        <Grid item xs={12}>
                            {batchesList}
                        </Grid>

                        {/* Individuals */}
                        {individualsList && (
                            <>
                                <Grid item xs={12} sx={{mt: 2, mb: 1}}>
                                    <Divider>
                                        <Typography variant="body2" sx={{fontWeight: "bold", color: "text.secondary"}}>
                                            Paquetes Individuales
                                        </Typography>
                                    </Divider>
                                </Grid>
                                <Grid item xs={12}>
                                    {individualsList}
                                </Grid>
                            </>
                        )}

                        {/* Expiry legend */}
                        <Grid item xs={12} sx={{mt: 2, mb: 1, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap"}}>
                            <Typography variant="caption" sx={{color: "warning.main", fontWeight: "bold"}}>● Vence en menos de 30 días</Typography>
                            <Typography variant="caption" sx={{color: "error.main", fontWeight: "bold"}}>● Vencido</Typography>
                        </Grid>

                        {/* Back button */}
                        <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mt: 1, mb: 3}}>
                            <Button onClick={goBack} variant="outlined" sx={{borderColor: Colors.primary, color: Colors.primary, textTransform: "none"}}>
                                Atrás
                            </Button>
                        </Grid>
                    </Grid>
                }
            </Box>
        </Box>
    );
}
